import { useMutation, useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { useSalesRepView } from 'features/salesRepView/SalesRepView.hook';
import Organization from 'infrastructure/Organization';
import { toastFlashMessage } from 'utils';
import { API } from 'utils/globalApiServices';
import {
  getQuestionsService,
  getTrainingModuleIngestion,
  createQuestionsService,
} from './ModuleAssessment.services';
import { useModuleAssessmentsAIStore } from './ObtainWithAI/ModuleAssessmentAI.store';

/** Retrieves the module assessment questions for the rep to answer or to review its results  */
export const useModuleAssessmentQuestionsQuery = ({
  module_uuid,
  clearPreviousAnswers = false,
}) => {
  const { isActive } = useSalesRepView();
  return useQuery({
    enabled: Boolean(module_uuid) && (Organization?.isRep() ? true : isActive),
    queryKey: ['MODULE_ASSESSMENT_QUESTIONS', module_uuid, clearPreviousAnswers],
    queryFn: async () => {
      const url = `/module/${module_uuid}/assessments/questions/`;
      const { data = [] } = await API.get(url);
      return clearPreviousAnswers ? data?.map((q) => ({ ...q, answer: {} })) : data;
    },
  });
};

/** Retrieves the module assessment for the admin, includes module and user information  */
export const useModuleAssessmentReviewQuery = ({ module_uuid, user_uuid }) => {
  const { isActive } = useSalesRepView();
  return useQuery({
    enabled:
      Boolean(module_uuid) && Boolean(user_uuid) && (Organization?.isRep() ? false : !isActive),
    queryKey: ['MODULE_ASSESSMENT_REVIEW', module_uuid, user_uuid],
    queryFn: async () => {
      const url = `/module/${module_uuid}/assessment/user/${user_uuid}/`;
      const data = await API.get(url);
      return data?.data || [];
    },
  });
};

/** Allows the rep to submit a module based assessment */
export const useSubmitModuleAssessmentMutation = () => {
  return useMutation({
    mutationFn: async ({ module_uuid, questions }) => {
      const url = `/module/${module_uuid}/assessments/submit/`;
      const data = await API.post(url, { questions });
      return data;
    },
  });
};

// Gets the current qualifying criteria, for configuration
export const useModuleCriteriaConfigQuery = () => {
  return useQuery({
    enabled: !Organization?.isRep(),
    queryKey: ['MODULE_ASSESSMENT_CRITERIA'],
    queryFn: async () => {
      const url = 'v2/organization/passing-criteria/list/';
      const data = await API.get(url);

      const item = data?.data?.criteria.find((c) => c.key === 'module_assessment_passing_criteria');
      const result = item
        ? {
            module_assessments: {
              rating_to_qualify: item?.min_value,
              uuid: item?.uuid,
            },
          }
        : {};
      return result;
    },
  });
};

// Sets a new qualifying criteria, for configuration
export const useModuleCriteriaConfigMutation = () => {
  return useMutation({
    mutationFn: async ({ criterion_uuid, min_value }) => {
      const url = `/v2/organization/passing-criteria/${criterion_uuid}/update/`;
      const data = await API.put(url, { min_value });
      return data;
    },
    onSuccess: (res) => toastFlashMessage(res?.message, 'success'),
  });
};

const verifyStatus = (list, module_uuid) => {
  const index = list.findIndex((item) => item.module_uuid === module_uuid);
  const status = () => (index !== -1 ? list[index].status : '');
  return status() === 'IN_PROGRESS' || status() === 'QUEUED' ? 5000 : false;
};

const formattedList = (list) => {
  return list?.map(({ question, options }) => {
    const result = { name: question };
    const feedback_questions = options
      .map((opt) => ({
        is_correct: opt.correct,
        name: opt.name,
      }))
      .sort((a, b) => (b.is_correct ? 1 : 0) - (a.is_correct ? 1 : 0)); // Coloca el true primero
    result.feedback_questions = feedback_questions;
    return result;
  });
};

/** Generate MCQs (Step 1) */
export const useCreateQuestionsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createQuestionsService,
    onSuccess: (response, request) => {
      if (request?.regenerate) {
        console.log('invalidate query');
        queryClient.invalidateQueries([
          'MODULE_ASSESSMENT_QUESTIONS_GENERATED_BY_AI',
          request?.module_uuid,
        ]);
      }
    },
  });
};

/** Obtain the 25 questions generated by AI ( Get MCQs - Step 2) */
export const useModuleAssessmentAIQuery = ({ module_uuid, enabled }) => {
  const setQuestions = useModuleAssessmentsAIStore((state) => state?.setQuestions);
  const questionsPerModule = useModuleAssessmentsAIStore((state) => state?.questionsPerModule);
  return useQuery({
    queryKey: ['MODULE_ASSESSMENT_QUESTIONS_GENERATED_BY_AI', module_uuid],
    queryFn: () => getQuestionsService({ module_uuid }),
    enabled,
    refetchInterval: () => {
      return verifyStatus(questionsPerModule, module_uuid);
    },
    refetchIntervalInBackground: true,
    onSuccess: (response) => {
      const questions = formattedList(response?.mcqs ?? []);
      const status = response?.status ?? 'IN_PROGRESS';
      const msg = response?.msg ?? '';
      const data = {
        module_uuid,
        status,
        msg,
        questions,
      };
      setQuestions(data);
    },
  });
};

/** Obtain the status of the  25 questions generated by AI and show a notification (STEP 3)  */
export const useModuleAssessmentStatusAIQuery = () => {
  const pendingQuestionsByModule = useModuleAssessmentsAIStore(
    (state) => state?.pendingQuestionsByModule
  );
  const questionsPerModule = useModuleAssessmentsAIStore((state) => state?.questionsPerModule);
  const setQuestions = useModuleAssessmentsAIStore((state) => state?.setQuestions);
  const removeItemFromList = useModuleAssessmentsAIStore((state) => state?.removeItemFromList);

  return useQueries({
    queries:
      Object.keys(pendingQuestionsByModule || []).length > 0
        ? pendingQuestionsByModule.map((module_uuid) => {
            return {
              queryKey: ['MODULE_ASSESSMENT_QUESTIONS_GENERATED_BY_AI_GLOBAL', module_uuid?.id],
              queryFn: () => getQuestionsService({ module_uuid: module_uuid?.id }),
              refetchInterval: () => {
                return verifyStatus(questionsPerModule, module_uuid?.id);
              },
              refetchIntervalInBackground: true,
              onSuccess: (response) => {
                const questions = formattedList(response?.mcqs ?? []);
                const status = response?.status ?? 'IN_PROGRESS';
                const msg = response?.msg ?? '';
                const data = {
                  module_uuid: module_uuid?.id,
                  status,
                  msg,
                  questions,
                };
                setQuestions(data);
                if (status !== 'IN_PROGRESS' && status !== 'QUEUED') {
                  toastFlashMessage(
                    `Your quiz for the training module ${module_uuid?.title} is now ready!`,
                    'success'
                  );
                  removeItemFromList(module_uuid?.id);
                }
              },
            };
          })
        : [],
  });
};

export const useGetTrainingModuleIngestion = ({ module_uuid, enabled }) => {
  return useQuery({
    queryKey: ['TRAINING_MODULE_INGESTION', module_uuid],
    queryFn: () => getTrainingModuleIngestion({ module_uuid }),
    enabled: enabled && Boolean(module_uuid),
  });
};
